import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Users, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const ReportAccessTab = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    loadData();
    // Refresh every 30 seconds to update countdown timers
    const interval = setInterval(loadUsers, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    await Promise.all([loadUsers(), loadSettings()]);
  };

  const loadUsers = async () => {
    try {
      const response = await axiosInstance.get('/admin/users/report-access');
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axiosInstance.get('/settings/system');
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const staffUsers = users.filter(u => u.role === 'Staff').map(u => u.user_id);
      setSelectedUsers(staffUsers);
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleGrantAccess = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    try {
      setActionLoading(true);
      const duration = settings?.report_access?.default_duration_hours || 2;
      await axiosInstance.post('/admin/users/grant-report-access', {
        user_ids: selectedUsers,
        duration_hours: duration
      });
      // Format duration message
      const durationMsg = duration < 1 
        ? `${Math.round(duration * 60)} minutes` 
        : `${duration} hour${duration !== 1 ? 's' : ''}`;
      toast.success(`Report access granted for ${durationMsg}`);
      setSelectedUsers([]);
      await loadUsers();
    } catch (error) {
      console.error('Error granting access:', error);
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : 'Failed to grant access')
        : 'Failed to grant access';
      toast.error(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAccess = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }

    try {
      setActionLoading(true);
      await axiosInstance.post('/admin/users/revoke-report-access', {
        user_ids: selectedUsers
      });
      toast.success('Report access revoked');
      setSelectedUsers([]);
      await loadUsers();
    } catch (error) {
      console.error('Error revoking access:', error);
      const errorMsg = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
            ? error.response.data.detail 
            : 'Failed to revoke access')
        : 'Failed to revoke access';
      toast.error(errorMsg);
    } finally {
      setActionLoading(false);
    }
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return null;
    
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const staffUsers = users.filter(u => u.role === 'Staff');
  const allStaffSelected = staffUsers.length > 0 && staffUsers.every(u => selectedUsers.includes(u.user_id));

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-xl shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6" />
              Quick Report Access Management
            </h2>
            <p className="text-purple-100 mt-1">Grant temporary report access to staff users</p>
          </div>
          <button
            onClick={loadUsers}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
        <div className="flex items-start gap-2">
          <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold">Temporary Access System</p>
            <p className="mt-1">
              Report access granted to staff expires automatically after <strong>
                {(settings?.report_access?.default_duration_hours || 2) < 1 
                  ? `${Math.round((settings?.report_access?.default_duration_hours || 2) * 60)} minutes`
                  : `${settings?.report_access?.default_duration_hours || 2} hours`
                }
              </strong>.
              Configure duration in System Settings tab.
            </p>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      <div className="bg-white rounded-xl shadow-md border-2 border-purple-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1">
            <input
              type="checkbox"
              checked={allStaffSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 flex-shrink-0"
            />
            <span className="text-sm font-semibold text-gray-700">
              Select All Staff <span className="text-purple-600">({selectedUsers.length} selected)</span>
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={handleGrantAccess}
              disabled={actionLoading || selectedUsers.length === 0}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 text-sm"
            >
              <CheckCircle className="w-4 h-4" />
              Grant Access
            </button>
            <button
              onClick={handleRevokeAccess}
              disabled={actionLoading || selectedUsers.length === 0}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
            >
              <XCircle className="w-4 h-4" />
              Revoke Access
            </button>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white rounded-xl shadow-md border-2 border-purple-200 overflow-hidden">
        {/* Mobile Card View */}
        <div className="block md:hidden">
          {users.map((u) => {
            const isSelected = selectedUsers.includes(u.user_id);
            const hasAccess = u.report_access?.enabled && u.report_access?.expires_at;
            const timeRemaining = hasAccess ? getTimeRemaining(u.report_access.expires_at) : null;
            const isExpired = timeRemaining === 'Expired';

            return (
              <div key={u.user_id} className={`p-4 border-b border-gray-200 ${isSelected ? 'bg-purple-50' : ''}`}>
                <div className="flex items-start gap-3">
                  {/* Checkbox */}
                  <div className="flex-shrink-0 pt-1">
                    {u.role === 'Staff' ? (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleSelectUser(u.user_id, e.target.checked)}
                        className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )}
                  </div>
                  
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 text-base truncate">{u.full_name}</p>
                        <p className="text-xs text-gray-500">@{u.username}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        u.role === 'Admin' ? 'bg-red-100 text-red-700' :
                        u.role === 'Manager' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {u.role}
                      </span>
                    </div>
                    
                    {/* Status and Time */}
                    <div className="space-y-1">
                      {u.role === 'Admin' || u.role === 'Manager' ? (
                        <div className="flex items-center gap-1 text-green-600 text-sm font-semibold">
                          <CheckCircle className="w-4 h-4" />
                          <span>Always Active</span>
                        </div>
                      ) : hasAccess && !isExpired ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1 text-green-600 text-sm font-semibold">
                            <CheckCircle className="w-4 h-4" />
                            <span>Active</span>
                          </div>
                          <div className="flex items-center gap-1 text-orange-600 text-sm">
                            <Clock className="w-4 h-4" />
                            <span>Expires in {timeRemaining}</span>
                          </div>
                          {u.report_access?.granted_by && (
                            <p className="text-xs text-gray-500">By: {u.report_access.granted_by}</p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-gray-400 text-sm">
                          <XCircle className="w-4 h-4" />
                          <span>No Access</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-purple-50">
              <tr>
                <th className="px-4 py-3 text-left w-12">
                  <input
                    type="checkbox"
                    checked={allStaffSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">User</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Role</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Expires In</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Granted By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((u) => {
                const isSelected = selectedUsers.includes(u.user_id);
                const hasAccess = u.report_access?.enabled && u.report_access?.expires_at;
                const timeRemaining = hasAccess ? getTimeRemaining(u.report_access.expires_at) : null;
                const isExpired = timeRemaining === 'Expired';

                return (
                  <tr key={u.user_id} className={`hover:bg-gray-50 ${isSelected ? 'bg-purple-50' : ''}`}>
                    <td className="px-4 py-3 w-12">
                      {u.role === 'Staff' ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectUser(u.user_id, e.target.checked)}
                          className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-gray-800 text-base">{u.full_name}</p>
                        <p className="text-xs text-gray-500">{u.username}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        u.role === 'Admin' ? 'bg-red-100 text-red-700' :
                        u.role === 'Manager' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {u.role === 'Admin' || u.role === 'Manager' ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm font-semibold">
                          <CheckCircle className="w-4 h-4" />
                          Always Enabled
                        </span>
                      ) : hasAccess && !isExpired ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm font-semibold">
                          <CheckCircle className="w-4 h-4" />
                          Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-gray-400 text-sm">
                          <XCircle className="w-4 h-4" />
                          No Access
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.role === 'Admin' || u.role === 'Manager' ? (
                        <span className="text-sm text-gray-500">—</span>
                      ) : hasAccess && !isExpired ? (
                        <span className="flex items-center gap-1 text-orange-600 text-sm font-semibold">
                          <Clock className="w-4 h-4" />
                          {timeRemaining}
                        </span>
                      ) : isExpired ? (
                        <span className="text-sm text-red-500">Expired</span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.report_access?.granted_by ? (
                        <span className="text-sm text-gray-600">{u.report_access.granted_by}</span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No users found</p>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <h4 className="font-semibold text-gray-800 mb-2">How it works:</h4>
        <ul className="list-disc list-inside space-y-1">
          <li>Select one or more staff users from the list</li>
          <li>Click "Grant Access" to give them temporary report access</li>
          <li>Access automatically expires after the configured duration</li>
          <li>Admin and Manager roles always have report access</li>
          <li>Countdown timers show remaining time for active access</li>
        </ul>
      </div>
    </div>
  );
};

export default ReportAccessTab;
